<template>
  <v-dialog
    :model-value="modelValue"
    max-width="720px"
    persistent
    scrollable
    data-testid="cfgv2-cimport-dialog"
    @update:model-value="$emit('update:modelValue', $event)"
  >
    <v-card>
      <v-card-title class="bg-deep-purple text-white d-flex align-center pa-4">
        <v-icon class="mr-2">mdi-import</v-icon>
        <div class="flex-grow-1">
          <div class="text-h6">นำเข้าชุดสูตรคำนวณจากไฟล์</div>
          <div v-if="configVersionLabel" class="text-caption text-white-50">
            นำเข้าลงคอนฟิก {{ configVersionLabel }}
          </div>
        </div>
        <v-btn
          icon
          variant="text"
          color="white"
          data-testid="cfgv2-cimport-close-btn"
          @click="close"
        >
          <v-icon>mdi-close</v-icon>
        </v-btn>
      </v-card-title>

      <v-card-text class="pa-6">
        <v-alert
          v-if="error"
          type="error"
          variant="tonal"
          density="comfortable"
          class="mb-4"
          data-testid="cfgv2-cimport-error"
        >
          {{ error }}
        </v-alert>

        <v-alert
          type="info"
          variant="tonal"
          density="comfortable"
          class="mb-4"
          data-testid="cfgv2-cimport-hint"
        >
          ไฟล์นี้จะถูกสร้างเป็น <strong>ชุดสูตรใหม่</strong> ใต้คอนฟิกนี้เสมอ และเป็น draft (ยังไม่ active) —
          ชุดสูตรที่ใช้งานอยู่จะไม่ถูกแตะ · สูตรจะถูกคอมไพล์ใหม่ที่เครื่องนี้ตอนนำเข้าจริง
        </v-alert>

        <!-- ── 1. เลือกไฟล์ / วาง JSON ─────────────────────────────── -->
        <div class="d-flex align-center flex-wrap ga-2">
          <v-btn
            variant="outlined"
            size="small"
            prepend-icon="mdi-file-upload-outline"
            data-testid="cfgv2-cimport-pick"
            @click="pickImportFile"
          >
            เลือกไฟล์ .json
          </v-btn>
          <span v-if="fileName" class="text-caption text-medium-emphasis" data-testid="cfgv2-cimport-filename">
            {{ fileName }}
          </span>
        </div>
        <input
          ref="importFile"
          type="file"
          accept="application/json,.json"
          class="d-none"
          data-testid="cfgv2-cimport-file"
          @change="onImportFileChosen"
        >

        <v-textarea
          v-model="importText"
          class="mt-3"
          label="หรือวาง JSON ที่นี่"
          rows="6"
          variant="outlined"
          density="compact"
          hide-details
          data-testid="cfgv2-cimport-text"
          @update:model-value="onTextInput"
        />

        <v-alert
          v-if="parseError"
          type="error"
          variant="tonal"
          density="comfortable"
          class="mt-3"
          data-testid="cfgv2-cimport-parse-error"
        >
          {{ parseError }}
        </v-alert>

        <!-- ที่มาของไฟล์ (อ่านจากตัวไฟล์เอง — ยังไม่ได้ยิง dry-run) -->
        <v-alert
          v-else-if="sourceLines.length"
          type="info"
          variant="tonal"
          density="comfortable"
          class="mt-3"
          icon="mdi-file-document-outline"
          data-testid="cfgv2-cimport-source"
        >
          <div class="text-subtitle-2 mb-1">ที่มาของไฟล์</div>
          <div v-for="line in sourceLines" :key="line.label" class="text-caption">
            {{ line.label }}: <strong>{{ line.value }}</strong>
          </div>
        </v-alert>

        <!-- ── 2. รหัสชุด / คำอธิบาย ────────────────────────────────── -->
        <v-row class="mt-2" dense>
          <v-col cols="12" md="5">
            <v-text-field
              v-model="versionCode"
              label="รหัสชุดใหม่"
              variant="outlined"
              density="comfortable"
              prepend-inner-icon="mdi-tag-outline"
              placeholder="C1"
              hint="เว้นว่าง = ใช้รหัสในไฟล์ · ถ้าชนของเดิมระบบจะต่อท้ายให้ (C1 → C1-2)"
              persistent-hint
              data-testid="cfgv2-cimport-code-input"
              @update:model-value="onCodeInput"
            />
          </v-col>
          <v-col cols="12" md="7">
            <v-text-field
              v-model="description"
              label="คำอธิบาย"
              variant="outlined"
              density="comfortable"
              prepend-inner-icon="mdi-text-box-outline"
              placeholder="รายละเอียดชุดสูตรนี้"
              hint="เว้นว่าง = ใช้คำอธิบายในไฟล์"
              persistent-hint
              data-testid="cfgv2-cimport-description-input"
              @update:model-value="onDescriptionInput"
            />
          </v-col>
        </v-row>

        <!-- ── 3. ผลการตรวจไฟล์ (dry-run) ───────────────────────────── -->
        <v-card
          v-if="preview"
          variant="tonal"
          class="mt-4"
          data-testid="cfgv2-cimport-preview"
        >
          <v-card-text class="pa-4">
            <div class="text-subtitle-2 mb-2">ผลการตรวจไฟล์</div>

            <div class="d-flex align-center flex-wrap ga-2 mb-3">
              <v-chip size="small" variant="flat" color="deep-purple" prepend-icon="mdi-tag">
                รหัสชุดที่จะได้: {{ preview.versionCode }}
              </v-chip>
              <v-chip size="small" variant="tonal" prepend-icon="mdi-function-variant">
                {{ preview.functionCount || 0 }} สูตร
              </v-chip>
              <v-chip
                v-if="(preview.compileErrorCount || 0) > 0"
                size="small"
                variant="tonal"
                color="warning"
                prepend-icon="mdi-alert-circle-outline"
              >
                คอมไพล์ไม่ผ่าน {{ preview.compileErrorCount }} สูตร
              </v-chip>
            </div>

            <v-alert
              v-if="preview.versionCodeAdjusted"
              type="warning"
              variant="tonal"
              density="compact"
              class="mb-3"
              data-testid="cfgv2-cimport-code-adjusted"
            >
              รหัสที่ระบุชนกับชุดสูตรเดิมในคอนฟิกนี้ — ระบบเปลี่ยนให้เป็น
              <strong>{{ preview.versionCode }}</strong> อัตโนมัติ
            </v-alert>

            <v-alert
              v-if="previewWarnings.length"
              type="warning"
              variant="tonal"
              density="comfortable"
              data-testid="cfgv2-cimport-warnings"
            >
              <div class="text-subtitle-2 mb-1">
                พบข้อควรระวัง {{ previewWarnings.length }} รายการ — นำเข้าต่อได้ แต่ควรอ่านก่อน
              </div>
              <ul class="cfgv2-cimport-warn-list">
                <li v-for="(w, i) in previewWarnings" :key="i" class="text-caption">{{ w }}</li>
              </ul>
            </v-alert>
            <v-alert
              v-else
              type="success"
              variant="tonal"
              density="comfortable"
              data-testid="cfgv2-cimport-ok"
            >
              ตรวจไฟล์แล้ว ไม่พบปัญหา — นำเข้าได้เลย ·
              การคอมไพล์เกิดขึ้นตอนนำเข้าจริงเท่านั้น จึงอาจพบสูตรที่คอมไพล์ไม่ผ่านเพิ่มได้
            </v-alert>
          </v-card-text>
        </v-card>

        <!-- ── 4. ผลการนำเข้าจริง ───────────────────────────────────── -->
        <!-- The parent normally closes this dialog as soon as `imported` fires, so this block is
             a belt-and-braces copy of what the page's snackbar says: a partial load must never be
             something the user has to guess at. -->
        <v-card
          v-if="importResult"
          variant="tonal"
          class="mt-4"
          data-testid="cfgv2-cimport-result"
        >
          <v-card-text class="pa-4">
            <div class="text-subtitle-2 mb-2">ผลการนำเข้า</div>
            <div class="d-flex align-center flex-wrap ga-2 mb-3">
              <v-chip size="small" variant="flat" color="success" prepend-icon="mdi-check">
                นำเข้าเป็นชุด {{ importResult.versionCode }}
              </v-chip>
              <v-chip size="small" variant="tonal" prepend-icon="mdi-function-variant">
                {{ importResult.functionCount || 0 }} สูตร
              </v-chip>
              <v-chip
                v-if="(importResult.compileErrorCount || 0) > 0"
                size="small"
                variant="tonal"
                color="error"
                prepend-icon="mdi-alert-circle-outline"
                data-testid="cfgv2-cimport-result-compile-errors"
              >
                คอมไพล์ไม่ผ่าน {{ importResult.compileErrorCount }} สูตร
              </v-chip>
            </div>
            <v-alert
              v-if="resultWarnings.length"
              type="warning"
              variant="tonal"
              density="comfortable"
              data-testid="cfgv2-cimport-result-warnings"
            >
              <div class="text-subtitle-2 mb-1">
                นำเข้าแล้ว แต่มีคำเตือน {{ resultWarnings.length }} รายการ
              </div>
              <ul class="cfgv2-cimport-warn-list">
                <li v-for="(w, i) in resultWarnings" :key="i" class="text-caption">{{ w }}</li>
              </ul>
            </v-alert>
          </v-card-text>
        </v-card>
      </v-card-text>

      <v-card-actions class="pa-4">
        <v-spacer />
        <v-btn
          variant="outlined"
          :disabled="checking || importing"
          data-testid="cfgv2-cimport-cancel-btn"
          @click="close"
        >
          <v-icon start>mdi-close</v-icon>
          ยกเลิก
        </v-btn>
        <v-btn
          color="deep-purple"
          variant="tonal"
          :disabled="!parsedBundle || importing"
          :loading="checking"
          data-testid="cfgv2-cimport-check-btn"
          @click="runDryRun"
        >
          <v-icon start>mdi-file-search-outline</v-icon>
          ตรวจไฟล์
        </v-btn>
        <v-btn
          color="deep-purple"
          variant="elevated"
          :disabled="!preview || checking"
          :loading="importing"
          data-testid="cfgv2-cimport-submit-btn"
          @click="runImport"
        >
          <v-icon start>mdi-import</v-icon>
          นำเข้าเลย
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
/**
 * Import a Tax Calculate V2 bundle (`.json` produced by the calc row's «ส่งออกเป็นไฟล์») as a NEW
 * calc version under ONE config version — the formula-side twin of
 * `components/TaxQuestionImportV2Dialog.vue`, deliberately the same shape so the two «นำเข้า»
 * buttons on the config tree behave identically.
 *
 * Contract with the parent (`views/TaxConfigurationV2.vue`):
 *   - the parent owns the target identity (`configVersionId`) and the snackbar;
 *   - this dialog reports its own errors inline (`v-alert`), never through a snackbar of its own;
 *   - `imported` fires AFTER the real (non-dry-run) call succeeded, carrying the whole
 *     `ImportCalcBundleResult`, and this dialog does NOT close itself — the parent decides,
 *     because it is the one that must reload the calc sub-tree and surface the warnings.
 *     «ยกเลิก» / «X» still emit `update:modelValue: false` normally.
 *
 * Two-step by design: «ตรวจไฟล์» runs the SAME server-side remap with `dryRun: true` (writes
 * nothing AND compiles nothing) and its result is what unlocks «นำเข้าเลย». Editing anything
 * afterwards — the pasted JSON, the code, the description — throws that preview away, because a
 * preview that no longer describes the text on screen is worse than no preview at all.
 *
 * A missing `fieldKey` or a formula that will not compile here never blocks the import — it comes
 * back in `warnings` / `compileErrorCount`, so it is shown, not swallowed: a silent partial load
 * is worse than a refusal.
 *
 * `configVersionId` (the row the Import button sits under) is ALWAYS the target — `bundle.source.*`
 * is untrusted display-only provenance and never resolves anything.
 */
import taxCalculateV2Api from '@/services/taxCalculateV2Api';

/** Envelope marker written by the export endpoint — sniffed before anything else is touched. */
const BUNDLE_KIND = 'lhb.taxcalc.v2.bundle';

export default {
  name: 'TaxCalcImportV2Dialog',
  props: {
    modelValue: { type: Boolean, default: false },
    /** target config version — the new calc set is created under THIS one */
    configVersionId: { type: Number, default: null },
    /** shown as the dialog subtitle (e.g. `2568 / V1`) */
    configVersionLabel: { type: String, default: '' },
    /** parent's suggestion for the new code, used until the file supplies its own */
    suggestedCode: { type: String, default: '' },
  },
  emits: ['update:modelValue', 'imported'],
  data() {
    return {
      importText: '',
      fileName: '',
      /** why the pasted/loaded text is not a usable bundle (set from `parsedBundle`) */
      parseError: '',
      /** API-level failure of the dry run / the import itself */
      error: '',
      /** `ImportCalcBundleResult` of the last dry run — gates «นำเข้าเลย» */
      preview: null,
      /** `ImportCalcBundleResult` of the REAL import — kept so warnings survive on screen */
      importResult: null,
      versionCode: '',
      description: '',
      /** stop the auto-prefill from the file once the user has typed their own */
      codeTouched: false,
      descriptionTouched: false,
      checking: false,
      importing: false,
    };
  },
  computed: {
    /**
     * The parsed bundle, or null while the text does not hold a usable one. Doubles as the
     * «ตรวจไฟล์» button's enable test — parsing here rather than on submit means a wrong file
     * (a question bundle, a Tax Config export, a Playground snapshot) is caught before the
     * server is called. Sets `parseError` as a side effect so the reason is visible.
     */
    parsedBundle() {
      const raw = (this.importText || '').trim();
      if (!raw) { this.parseError = ''; return null; }
      let bundle;
      try {
        bundle = JSON.parse(raw);
      } catch (e) {
        this.parseError = 'ไฟล์นี้ไม่ใช่ JSON ที่อ่านได้';
        return null;
      }
      if (!bundle || typeof bundle !== 'object' || Array.isArray(bundle)) {
        this.parseError = 'ไฟล์นี้ไม่ใช่ไฟล์ชุดสูตร Tax Calculate V2';
        return null;
      }
      if (bundle.kind !== BUNDLE_KIND) {
        this.parseError = bundle.kind
          ? `ไฟล์นี้ไม่ใช่ไฟล์ชุดสูตร Tax Calculate V2 — ไฟล์นี้เป็น "${bundle.kind}"`
          : 'ไฟล์นี้ไม่ใช่ไฟล์ชุดสูตร Tax Calculate V2 — ไม่พบ kind ในไฟล์';
        return null;
      }
      if (!Array.isArray(bundle.functions) || bundle.functions.length === 0) {
        this.parseError = 'ไฟล์นี้ไม่มีสูตรอยู่เลย (functions ว่าง)';
        return null;
      }
      this.parseError = '';
      return bundle;
    },
    /** `source.*` of the loaded file — every null field is DROPPED, never rendered as "null". */
    sourceLines() {
      const b = this.parsedBundle;
      const src = (b && b.source) || null;
      if (!src) return [];
      const lines = [];
      const push = (label, value) => {
        if (value === null || value === undefined || String(value).trim() === '') return;
        lines.push({ label, value: String(value) });
      };
      push('ชุดสูตรต้นทาง', src.calcVersionCode);
      push('คอนฟิกต้นทาง', src.configVersionCode);
      push('ปีภาษี', src.taxYearCode);
      push('ปี พ.ศ.', src.beYear);
      push('คำอธิบาย', src.description);
      const functions = Array.isArray(b.functions) ? b.functions.length : 0;
      lines.push({ label: 'ในไฟล์', value: `${functions} สูตร` });
      if (b.exportedAt) push('ส่งออกเมื่อ', b.exportedAt);
      if (b.exportedBy) push('ส่งออกโดย', b.exportedBy);
      return lines;
    },
    previewWarnings() {
      const w = this.preview && this.preview.warnings;
      return Array.isArray(w) ? w : [];
    },
    resultWarnings() {
      const w = this.importResult && this.importResult.warnings;
      return Array.isArray(w) ? w : [];
    },
  },
  watch: {
    modelValue(open) {
      if (open) this.resetState();
    },
    /** Prefill the code/description from the file, but never over something the user typed. */
    parsedBundle(bundle) {
      const src = (bundle && bundle.source) || {};
      if (!this.codeTouched) {
        this.versionCode = this.normalizeCode(src.calcVersionCode || this.suggestedCode || '');
      }
      if (!this.descriptionTouched) {
        this.description = src.description || '';
      }
    },
  },
  methods: {
    normalizeCode(value) {
      return String(value || '').replace(/\s+/g, '').toUpperCase();
    },
    resetState() {
      this.importText = '';
      this.fileName = '';
      this.parseError = '';
      this.error = '';
      this.preview = null;
      this.importResult = null;
      this.versionCode = this.normalizeCode(this.suggestedCode);
      this.description = '';
      this.codeTouched = false;
      this.descriptionTouched = false;
      this.checking = false;
      this.importing = false;
      const el = this.$refs.importFile;
      if (el) el.value = '';
    },
    close() {
      this.$emit('update:modelValue', false);
    },
    pickImportFile() {
      const el = this.$refs.importFile;
      // Clearing FIRST so re-picking the SAME file still fires `change` (the browser skips the
      // event when the value is unchanged).
      if (el) { el.value = ''; el.click(); }
    },
    onImportFileChosen(evt) {
      const file = evt && evt.target && evt.target.files && evt.target.files[0];
      if (!file) return;
      this.fileName = file.name || '';
      this.error = '';
      this.preview = null;
      this.importResult = null;
      const reader = new FileReader();
      reader.onload = () => { this.importText = String(reader.result || ''); };
      reader.onerror = () => {
        this.importText = '';
        this.parseError = 'อ่านไฟล์ไม่สำเร็จ';
      };
      reader.readAsText(file);
    },
    /** Any edit invalidates the dry run — the preview must describe the text that is there NOW. */
    onTextInput() {
      this.preview = null;
      this.importResult = null;
      this.error = '';
    },
    onCodeInput(value) {
      this.codeTouched = true;
      this.versionCode = this.normalizeCode(value);
      // The code is part of what the dry run decided (collision → `C1-2`), so re-check is required.
      this.preview = null;
    },
    onDescriptionInput() {
      this.descriptionTouched = true;
      // The description travels with the same request the dry run validated — re-check it too.
      this.preview = null;
    },
    /** Shared payload of both calls — the only difference between them is `dryRun`. */
    buildRequest(dryRun) {
      return {
        bundle: this.parsedBundle,
        // 🔴 trailing capital `ID` — the service forwards this key verbatim to ASP.NET.
        taxConfigVersionID: this.configVersionId,
        versionCode: this.normalizeCode(this.versionCode) || null,
        description: (this.description || '').trim() || null,
        dryRun,
      };
    },
    async runDryRun() {
      if (!this.parsedBundle || this.checking || this.importing) return;
      if (!(Number(this.configVersionId) > 0)) {
        this.error = 'ไม่รู้ว่าจะนำเข้าลงคอนฟิกไหน — ปิดหน้าต่างแล้วกดปุ่มนำเข้าใหม่อีกครั้ง';
        return;
      }
      this.checking = true;
      this.error = '';
      this.preview = null;
      this.importResult = null;
      try {
        this.preview = await taxCalculateV2Api.importBundle(this.buildRequest(true));
      } catch (e) {
        this.error = (e && e.message) || 'ตรวจไฟล์ไม่สำเร็จ';
      } finally {
        this.checking = false;
      }
    },
    async runImport() {
      if (!this.preview || this.checking || this.importing) return;
      this.importing = true;
      this.error = '';
      try {
        const result = await taxCalculateV2Api.importBundle(this.buildRequest(false));
        this.importResult = result;
        // Deliberately NOT closing here — the parent reloads the list, shows the warnings and
        // decides when this dialog goes away.
        this.$emit('imported', result);
      } catch (e) {
        this.error = (e && e.message) || 'นำเข้าชุดสูตรไม่สำเร็จ';
      } finally {
        this.importing = false;
      }
    },
  },
};
</script>

<style scoped>
.text-white-50 {
  opacity: 0.8;
}
.cfgv2-cimport-warn-list {
  margin: 0;
  padding-left: 18px;
}
</style>
